import { useEffect, useState } from 'react';
import { getCachedAvatar, setCachedAvatar } from '@/lib/avatar-cache';

/**
 * Avatar that never flashes broken/initials on a failed load.
 *
 * - On mount it renders the last *confirmed-good* avatar URL for this user
 *   (from IndexedDB), so a fresh login shows the cached avatar immediately.
 * - When a new candidate `url` arrives it is preloaded off-screen; the visible
 *   avatar only swaps once that image actually loads, and the new URL is then
 *   persisted as the confirmed-good one.
 * - If the candidate fails to load, the cached avatar is kept untouched.
 * - Cache is keyed by `userId`, so a different login never inherits another
 *   user's avatar; logout clears it (see AuthContext -> clearCachedAvatar).
 *
 * Sizing/shape come from `className` (e.g. "h-6 w-6 rounded-full") so this is a
 * drop-in for the existing nav avatar.
 */
interface CachedAvatarProps {
  userId?: string | null;
  /** Canonical candidate URL (user_profiles.avatar_url, fallback auth metadata). */
  url?: string | null;
  name?: string | null;
  className?: string;
  fallbackClassName?: string;
  alt?: string;
}

function getInitials(name?: string | null): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function CachedAvatar({
  userId,
  url,
  name,
  className = '',
  fallbackClassName = 'bg-foreground/10 text-foreground',
  alt,
}: CachedAvatarProps) {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);

  // Hydrate from cache whenever the user changes — show last-good instantly.
  useEffect(() => {
    let cancelled = false;
    setRenderFailed(false);
    if (!userId) {
      setDisplayUrl(null);
      return;
    }
    getCachedAvatar(userId).then((cached) => {
      if (!cancelled && cached) setDisplayUrl((cur) => cur ?? cached);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Confirm-then-swap: preload a new candidate; only adopt it once it loads.
  useEffect(() => {
    if (!userId || !url || url === displayUrl) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setDisplayUrl(url);
      setRenderFailed(false);
      void setCachedAvatar(userId, url);
    };
    img.onerror = () => {
      /* keep the last confirmed-good avatar — do NOT overwrite */
    };
    img.src = url;
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [userId, url, displayUrl]);

  if (displayUrl && !renderFailed) {
    return (
      <img
        src={displayUrl}
        alt={alt ?? name ?? ''}
        className={`object-cover ${className}`}
        onError={() => setRenderFailed(true)}
      />
    );
  }

  return (
    <div
      className={`flex items-center justify-center font-bold ${fallbackClassName} ${className}`}
      aria-label={alt ?? name ?? undefined}
    >
      {getInitials(name)}
    </div>
  );
}
